import React, { memo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import { ICategoriesReasons, IOption } from 'components/Select/interface';
import { Column, Row } from 'components/Layout/Grid';
import Text from 'components/Text';
import Select from 'components/Select';
import SwitchComponent from 'components/SwitchComponent';

import { TicketCategoryTypeNumber, ILegalPersonType, EnumCategories, EnumReason, reasonsToHideProtocolsConsumerManual } from 'models/Ticket';
import queryString from 'query-string';
import TicketServices from 'services/ticket.service';
import { hideClientTypeProtocolCategories, hideEmployeeProtocolCategories } from '../interface';

import { useLoadCte } from 'hooks/useLoadCte';
import { useOpenProtocolContext } from 'contexts/OpenProtocol';
import { useCustomerDataContext } from 'contexts/OpenProtocol/CustomerData/CustomerDataContext';
import { CategoryReasonFilterProps } from 'components/SimpleForm/ProtocolCategorySimple/interface';
import { categoryToDisableArray } from './interface';

const ProtocolCategory: React.FC<any> = (props) => {

  const {
    protocolCategory,
    setProtocolCategory,
    ticketReason,
    setTicketReason,
    protocolClassification,
    setProtocolClassification,
    legalPerson
  } = props;

  const { hasCte } = useLoadCte();
  const { isDisableCte, nameBrandSelected } = useOpenProtocolContext();
  const { customerProtocol } = useCustomerDataContext();

  const user = useSelector((state: RootState) => state.user);

  const _protocolService = new TicketServices();
  const location = useLocation();

  const isEditing = !!location?.state?.protocolNumber;

  const [allCategories, setAllCategories] = useState<Array<IOption>>([]);
  const [allCategoriesCompleted, setAllCategoriesCompleted] = useState<Array<IOption>>([]);
  const [allReasons, setAllReasons] = useState<Array<IOption>>([]);
  const [allReasonsCompleted, setAllReasonsCompleted] = useState<Array<IOption>>([]);
  const [allClassifications, setAllClassifications] = useState<Array<IOption>>([]);

  useEffect(() => {
    getCategoriesReasons();
  }, []);

  useEffect(() => {
    if (allCategoriesCompleted?.length > 0) {
      hideCategory();
    }
  }, [allCategoriesCompleted, user]);

  async function getCategoriesReasons() {
    const options: ICategoriesReasons = await _protocolService.getCategoriesReasons();
    const { data } = options.data;

    sortProtocolArray(data.categories);
    setAllClassifications(data.classifications);

    if (!isEditing) {
      data.reasons = data.reasons.filter((reasons: CategoryReasonFilterProps) =>
        !reasonsToHideProtocolsConsumerManual.includes(reasons.label.toUpperCase()));
    }

    setAllReasons(data.reasons);
    setAllReasonsCompleted(data.reasons);

    if (isEditing) {
      let index: number = 0;

      switch (protocolCategory) {
        case 'DEVOLUÇÃO':
        case '1':
          index = data.categories.findIndex(x => x.value === '1');
          setAllCategories([data.categories[index]]);
          break;
        case 'RECLAMAÇÃO TÉCNICA':
        case '7':
          index = data.categories.findIndex(x => x.value === '7');
          setAllCategories([data.categories[index]]);
          break;
        case 'OCORRÊNCIA TRANSPORTADORA':
        case '11':
          index = data.categories.findIndex(x => x.value === '11');
          setAllCategories([data.categories[index]]);
          break;
        default:
          setAllCategoriesCompleted(data.categories);
          break;
      }
    } else {
      setAllCategoriesCompleted(data.categories);
    }
  }

  function sortProtocolArray(array: Array<IOption>) {
    array.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }

      return 0;
    });
  }

  const hideCategory = () => {
    let indexCategories: number = 0;

    const categoriesToHide: string[] = user?.userType === 'customer'
      ? hideClientTypeProtocolCategories
      : hideEmployeeProtocolCategories;

    categoriesToHide.forEach((category: string) => {
      indexCategories = allCategoriesCompleted.findIndex(x => x.label === category);
      if (indexCategories !== -1) {
        allCategoriesCompleted.splice(indexCategories, 1);
      }
    });

    setAllCategories(allCategoriesCompleted);
  };

  useEffect(() => {
    if (protocolCategory === EnumCategories.LOGISTICA && !isEditing) {
      setAllReasons(allReasonsCompleted.filter(
        x => x.label !== 'PREVISÃO DE ENTREGA' &&
          x.label !== 'ATRASO DE ENTREGA'
      ));
    }
  }, [protocolCategory]);

  function disableProtocolClassification() {
    
    if (protocolCategory === EnumCategories.LOGISTICA) return ticketReason !== '4';
    
    if (protocolCategory === EnumCategories.DEVOLUCAO) return ticketReason === '8';
    
    const categories = ['1', '5', '6', '7'];
    return !(categories.indexOf(protocolCategory) > -1 && ticketReason !== '');
  }

  function SetTicketCategoryFromUlr() {
    const params = queryString.parse(location.search);
    if (
      params.protocolCategory &&
      params.protocolCategory !== '' &&
      !protocolCategory
    ) {
      setProtocolCategory(
        TicketCategoryTypeNumber[params.protocolCategory as any]?.toString() ||
        ''
      );
    }
  }

  SetTicketCategoryFromUlr();

  useEffect(() => {
    props.setDisableProduct(false);
    props.setDisableOrder(true);
    isDisableCte(true);

    if (legalPerson === ILegalPersonType.JURIDICAL_PERSON) {
      if (protocolCategory === EnumCategories.COMERCIAL)
        props.setDisableProduct(!['2', '35'].includes(ticketReason));
      else
        props.setDisableProduct(
          ['1', '2', '3', '4', '5', '6', '7'].includes(protocolCategory)
        );

      props.setDisableOrder(ticketReason === EnumReason.INSTRUCAO_DE_USO);
      props.setDisableProduct(ticketReason === EnumReason.CARTA_DE_TROCA || protocolCategory === EnumCategories.RECLAMACAO_TECNICA);

      hideReasonSendingCte();

      conditionHideCteLinkType();
    }

    if (legalPerson === ILegalPersonType.NATURAL_PERSON)
      setAllReasons(allReasonsCompleted.filter(x =>
        x.label !== 'ENVIO DE CTE' &&
        x.label !== 'ENVIO XML DE CTE'));

    if (legalPerson === ILegalPersonType.NATURAL_PERSON &&
      protocolCategory === EnumCategories.SOLICITACAO_DE_INFORMACAO_SERVICO)
      setAllReasons(allReasonsCompleted.filter(x => x.label !== 'CARTA DE TROCA'));

  }, [
    legalPerson,
    protocolCategory,
    ticketReason,
    allReasonsCompleted,
    nameBrandSelected,
    hasCte,
    customerProtocol
  ]);

  const hideReasonSendingCte = () => {
    const isEmployeeDoesNotHaveCte = user?.userType === "employee" &&
      !customerProtocol.useElectronicBillLading;

    if (!hasCte || isEmployeeDoesNotHaveCte)
      setAllReasons(allReasonsCompleted.filter(x =>
        x.label !== 'ENVIO DE CTE' &&
        x.label !== 'ENVIO XML DE CTE'));
    else
      setAllReasons(allReasonsCompleted);
  };

  const conditionHideCteLinkType = () => {

    const isVisibleOnlyCte = visibleOnlyLinkCte();
    const isVisibleCte = visibleLinkCte();

    if (isVisibleOnlyCte) {
      isDisableCte(false);
      props.setDisableOrder(true);
      props.setDisableProduct(true);
    }

    if (isVisibleCte)
      isDisableCte(false);
  };

  const visibleOnlyLinkCte = () => {

    const reasonsToShowLinkCte = [
      EnumReason.ENVIO_DE_CTE,
      EnumReason.ENVIO_XML_DE_CTE
    ];

    if (user?.userType === "employee")
      return customerProtocol.useElectronicBillLading &&
        reasonsToShowLinkCte.includes(ticketReason);

    if (hasCte && user?.userType === "customer")
      return reasonsToShowLinkCte.includes(ticketReason);

    return false;
  };

  const visibleLinkCte = () => {
    if (user?.userType === "employee")
      return customerProtocol.useElectronicBillLading && nameBrandSelected === "Healthlog";

    if (hasCte && user?.userType === "customer")
      return nameBrandSelected === "Healthlog";

    return false;
  };

  const disableCategories = () => {
    const isFindCategoriesDisable = categoryToDisableArray.includes(protocolCategory);

    if (protocolCategory === EnumCategories.LOGISTICA &&
      ticketReason === EnumReason.FALTA_DE_VOLUME) {
      return true;
    }

    return isFindCategoriesDisable;
  };

  return (
    <Row className="flex-md-column">
      <Column className="flex-1">
        <Text className="mb-2 color-blue-gray" size={12}>
          Categoria do Protocolo
        </Text>
        <Select
          stateName="protocolCategory"
          onChange={(e) => setProtocolCategory(e.target.value)}
          className="w-100"
          name="Categoria do protocolo"
          id="category-protocol-select"
          value={protocolCategory}
          required
          options={allCategories}
          disabled={isEditing && (disableCategories())}
        />
      </Column>
      <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
        <Text className="mb-2 color-blue-gray" size={12}>
          Motivo do Protocolo
        </Text>
        <Select
          stateName="ticketReason"
          onChange={(e) => setTicketReason(e.target.value)}
          className="w-100"
          disabled={!protocolCategory}
          name="Motivo do protocolo"
          subjection={protocolCategory}
          id="reason-protocol-select"
          value={ticketReason}
          required
          options={allReasons}
        />
      </Column>
      <SwitchComponent roles={['administrator', 'member']} type={['employee']}>
        <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
          <Text className="mb-2 color-blue-gray" size={12}>
            Classificação do Protocolo
          </Text>
          <Select
            stateName="protocolClassification"
            onChange={(e) => setProtocolClassification(e.target.value)}
            className="w-100"
            name="Classificação do protocolo"
            id="classification-protocol-select"
            disabled={disableProtocolClassification()} // ticketReason ? false : true
            value={disableProtocolClassification() ? '' : protocolClassification}
            subjection={(protocolCategory === '6' || protocolCategory === '7') && ticketReason}
            options={allClassifications}
            category={protocolCategory}
          />
        </Column>
      </SwitchComponent>
      <SwitchComponent roles={['administrator', 'member']} type={['customer']}>
        <Row className="flex-1 ml-4 ml-md-0" />
      </SwitchComponent>
    </Row>
  );
};

export default memo(ProtocolCategory);




